import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { TyreAPI, TyreDto } from "../api/tyreApi";
import { BasketAPI } from "../api/basketApi";
import { ShoppingCart, Plus, Minus, ArrowLeft } from "lucide-react";

const TyrePage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tyre, setTyre] = useState<TyreDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addingToCart, setAddingToCart] = useState(false);
  const [inCart, setInCart] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [cartItemId, setCartItemId] = useState<number | null>(null);

  useEffect(() => {
    const loadTyre = async () => {
      if (!id) {
        setError("ID шины не указан");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const tyreData = await TyreAPI.getTyreById(parseInt(id));
        setTyre(tyreData);
        await checkCartStatus(tyreData);
      } catch (err) {
        console.error("Ошибка загрузки шины:", err);
        setError("Шина не найдена");
      } finally {
        setLoading(false);
      }
    };

    loadTyre();
  }, [id]);

  const checkCartStatus = async (tyreData: TyreDto) => {
    try {
      const items = await BasketAPI.getItems();
      const possibleCodes = [
        tyreData.uniqueSKU,
        tyreData.vendorSKU,
        `TYRE_${tyreData.id}`
      ].filter(Boolean);
      
      const item = items.find((i: any) => 
        possibleCodes.includes(i.code) || 
        i.code?.includes(`TYRE_${tyreData.id}`)
      );
      
      if (item) {
        setInCart(true);
        setQuantity(item.count);
        setCartItemId(item.id);
      } else {
        setInCart(false);
        setQuantity(1);
        setCartItemId(null);
      }
    } catch (error) {
      console.error("Ошибка проверки корзины:", error);
    }
  };

  const handleAddToCart = async () => {
    if (!tyre || addingToCart || inCart) return;

    try {
      setAddingToCart(true);
      const result = await BasketAPI.addTyreItem(tyre.id, quantity);
      if (result) {
        setInCart(true);
        setCartItemId(result.id);
      }
    } catch (error) {
      console.error("Ошибка добавления в корзину:", error);
    } finally {
      setAddingToCart(false);
    }
  };

  const handleUpdateQuantity = async (newQuantity: number) => {
    if (!tyre || newQuantity < 1 || newQuantity > tyre.residue || !cartItemId) return;

    try {
      await BasketAPI.updateItem(cartItemId, newQuantity);
      setQuantity(newQuantity);
    } catch (error) {
      console.error("Ошибка обновления количества:", error);
      await checkCartStatus(tyre);
    }
  };

  const formatTyreSize = (tyre: TyreDto) => {
    return `${tyre.width}/${tyre.profile} R${tyre.diameter}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Загрузка...</p>
        </div>
      </div>
    );
  }

  if (error || !tyre) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-xl mb-4">{error || "Шина не найдена"}</p>
          <button
            onClick={() => navigate('/tyres')}
            className="text-blue-600 hover:text-blue-800 underline"
          >
            Вернуться к каталогу шин
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 w-full flex justify-center">
      <div className="w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Кнопка назад */}
        <button
          onClick={() => navigate('/tyres')}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Вернуться к каталогу</span>
        </button>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-6 md:p-8">
            {/* Левая колонка - изображение */}
            <div>
              {tyre.image ? (
                <img
                  src={tyre.image}
                  alt={tyre.name || "Шина"}
                  className="w-full h-auto rounded-lg object-contain max-h-96 mx-auto"
                />
              ) : (
                <div className="w-full h-64 bg-gray-100 rounded-lg flex flex-col items-center justify-center text-gray-400">
                  <svg className="w-24 h-24 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm">Изображение отсутствует</span>
                </div>
              )}
            </div>

            {/* Правая колонка - информация */}
            <div className="space-y-6">
              <div>
                <h1 className="text-3xl font-bold mb-2">{tyre.brand}</h1>
                <p className="text-xl text-gray-600 mb-2">{tyre.name}</p>
                {tyre.model && (
                  <p className="text-sm text-gray-500">Модель: {tyre.model}</p>
                )}
              </div>

              <div className="border-t pt-6">
                <h2 className="text-2xl font-semibold mb-4">Основные характеристики</h2>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Размер:</span>
                    <span className="font-semibold text-lg">{formatTyreSize(tyre)}</span>
                  </div>
                  {tyre.loadIndex && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Индекс нагрузки:</span>
                      <span className="font-semibold">{tyre.loadIndex}</span>
                    </div>
                  )}
                  {tyre.speedIndex && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Индекс скорости:</span>
                      <span className="font-semibold">{tyre.speedIndex}</span>
                    </div>
                  )}
                  {tyre.season && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Сезон:</span>
                      <span className="font-semibold">{tyre.season}</span>
                    </div>
                  )}
                  {tyre.spike && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Шип:</span>
                      <span className="font-semibold">{tyre.spike}</span>
                    </div>
                  )}
                  {tyre.strength && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Усиленность:</span>
                      <span className="font-semibold">{tyre.strength}</span>
                    </div>
                  )}
                  {tyre.autoType && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Тип авто:</span>
                      <span className="font-semibold">{tyre.autoType}</span>
                    </div>
                  )}
                </div>
              </div>

              {(tyre.yearOfOrigin || tyre.countryOfOrigin) && (
                <div className="border-t pt-6">
                  <h2 className="text-2xl font-semibold mb-4">Дополнительная информация</h2>
                  <div className="space-y-3">
                    {tyre.yearOfOrigin && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Год выпуска:</span>
                        <span className="font-semibold">{tyre.yearOfOrigin}</span>
                      </div>
                    )}
                    {tyre.countryOfOrigin && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Страна производства:</span>
                        <span className="font-semibold">{tyre.countryOfOrigin}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-4xl font-bold text-red-600">{tyre.price.toLocaleString()} ₽</p>
                    <p className="text-sm text-gray-600 mt-2">
                      Остаток: {tyre.residue} {tyre.residue === 1 ? "шт." : "шт."}
                    </p>
                  </div>
                </div>
                {inCart ? (
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleUpdateQuantity(quantity - 1)}
                      disabled={quantity <= 1}
                      className="flex items-center justify-center w-12 h-12 rounded-md border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Minus className="w-5 h-5" />
                    </button>
                    <input
                      type="number"
                      min="1"
                      max={tyre.residue}
                      value={quantity}
                      onChange={(e) => {
                        const newQuantity = parseInt(e.target.value) || 1;
                        if (newQuantity >= 1 && newQuantity <= tyre.residue) {
                          handleUpdateQuantity(newQuantity);
                        }
                      }}
                      className="flex-1 text-center border rounded-md px-4 py-3 text-lg font-semibold"
                    />
                    <button
                      onClick={() => handleUpdateQuantity(quantity + 1)}
                      disabled={quantity >= tyre.residue}
                      className="flex items-center justify-center w-12 h-12 rounded-md border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={handleAddToCart}
                    disabled={addingToCart || tyre.residue === 0}
                    className={`w-full py-4 px-6 rounded-md font-semibold text-lg flex items-center justify-center gap-3 transition-colors ${
                      addingToCart
                        ? "bg-gray-400 text-white cursor-not-allowed"
                        : tyre.residue === 0
                        ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                        : "bg-gray-800 text-white hover:bg-gray-900"
                    }`}
                  >
                    {addingToCart ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Добавление...
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="w-6 h-6" />
                        Добавить в корзину
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TyrePage;

